import React, { useState } from "react";
import {
  Box,
  List,
  ListItem,
  ListItemText,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemButton,
  TextField,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { FILE_TYPE_ICONS } from "../../../../utils/fileUtils";
import {
  useAddFolderMutation,
  useDeleteQMFolderMutation,
  useMoveFileMutation,
} from "../../../../features/fileManager/fileManagerApiSlice";
import useCurrentOffice from "../../../../hooks/useCurrentOffice";
import { useDispatch } from "react-redux";
import { setDialog } from "../../../../features/dialog/dialogSlice";

const FolderTreeView = ({ data, onFileSelect }) => {
  const [open, setOpen] = useState([]);
  const [draggingFile, setDraggingFile] = useState(null);
  const [deleteFolder, setDeleteFolder] = useState(null);
  const [openCreateFolderDialog, setOpenCreateFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [moveTarget, setMoveTarget] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [deleteQMFolder] = useDeleteQMFolderMutation();
  const [moveFile] = useMoveFileMutation();
  const [addFolder] = useAddFolderMutation();
  const { office: officeRef } = useCurrentOffice();
  const dispatch = useDispatch();

  const handleClick = (node) => {
    setOpen(
      open.includes(node.id)
        ? open.filter((item) => item !== node.id)
        : [...open, node.id]
    );
  };

  const handleClickFile = (file) => {
    onFileSelect(file);
  };

  const handleDragStart = (file) => {
    setDraggingFile(file);
  };

  const handleDrop = async (folder) => {
    if (draggingFile && folder.type === "folder") {
      try {
        let result = await moveFile({
          fileId: draggingFile.fileDataId,
          targetFolderId: folder.id,
          officeRef,
        }).unwrap();

        if (result?.status === "SUCCESS") {
          dispatch(
            setDialog({
              title: `Datei verschoben!`,
              type: "SUCCESS",
              data: {
                successText: `Die Datei wurde erfolgreich in "${folder.name}" verschoben.`,
              },
            })
          );
        }
      } catch (error) {
        console.error("Error moving file:", error);
      }
      setDraggingFile(null);
    }
  };

  const handleDeleteFolder = (folder) => {
    setDeleteFolder(folder);
    setShowDeleteDialog(true);
  };

  const extractFileDataIds = (folder) => {
    let fileIds = [];
    function traverse(node) {
      if (!node.children) return;
      for (const child of node.children) {
        if (child.type === "file" && child.fileDataId) {
          fileIds.push(child.fileDataId);
        } else if (child.type === "folder") {
          traverse(child);
        }
      }
    }
    traverse(folder);
    return fileIds;
  };

  const confirmDeleteFolder = async (moveFiles) => {
    setShowDeleteDialog(false);

    if (moveFiles) {
      setShowMoveDialog(true);
    } else {
      const fileDataIds = extractFileDataIds(deleteFolder);
      try {
        let result = await deleteQMFolder({
          folderPath: deleteFolder.id,
          officeRef,
          fileDataIds,
        }).unwrap();

        if (
          result?.message === "Folder and associated files deleted successfully"
        ) {
          dispatch(
            setDialog({
              title: `${deleteFolder.name} wurde erfolgreich gelöscht!`,
              type: "SUCCESS",
              data: {
                successText: `${deleteFolder.name} wurde erfolgreich gelöscht! ${result.deletedFiles} Dateien entfernt.`,
              },
            })
          );
        }
      } catch (error) {
        console.error("Error deleting folder:", error);
      }
    }
  };

  const confirmMoveFiles = async () => {
    if (!deleteFolder || !moveTarget) return;

    try {
      const fileIds = extractFileDataIds(deleteFolder);
      let moveResults = await Promise.allSettled(
        fileIds.map((fileId) =>
          moveFile({
            fileId,
            targetFolderId: moveTarget,
            officeRef,
          }).unwrap()
        )
      );

      const successfulMoves = moveResults.filter(
        (res) => res.status === "fulfilled"
      ).length;
      console.log(`Successfully moved ${successfulMoves} files`);

      try {
        let result = await deleteQMFolder({
          folderPath: deleteFolder.id,
          officeRef,
          fileDataIds: [],
        }).unwrap();

        if (
          result?.message === "Folder and associated files deleted successfully"
        ) {
          dispatch(
            setDialog({
              title: `${deleteFolder.name} wurde erfolgreich geleert!`,
              type: "SUCCESS",
              data: {
                successText: `${deleteFolder.name} wurde erfolgreich geleert und gelöscht! ${result.deletedFiles} Dateien entfernt.`,
              },
            })
          );
        }
      } catch (error) {
        console.error("Error deleting folder:", error);
      }

      setShowMoveDialog(false);
    } catch (error) {
      console.error("Error in confirmMoveFiles:", error);
    }
  };

  const flattenFolders = (nodes, depth = 0, excludeFolderId = null) => {
    let flattened = [];
    for (const node of nodes) {
      if (node.type === "folder" && node.id !== excludeFolderId) {
        flattened.push({ ...node, depth });
        if (node.children) {
          flattened = flattened.concat(
            flattenFolders(node.children, depth + 1, excludeFolderId)
          );
        }
      }
    }
    return flattened;
  };

  const handleAddFolder = async () => {
    const newFolder = {
      id: `${openCreateFolderDialog}`,
      name: newFolderName,
      type: "folder",
      children: [],
      officeRef,
    };

    console.log("newFolder :>> ", newFolder);

    try {
      let result = await addFolder(newFolder).unwrap();
      if (result?.status === "SUCCESS") {
        dispatch(
          setDialog({
            title: `Ordner erstellt!`,
            type: "SUCCESS",
            data: {
              successText: `Der Ordner "${newFolderName}" wurde erfolgreich erstellt.`,
            },
          })
        );
      }
    } catch (error) {
      console.error("Error creating folder:", error);
    }

    setNewFolderName("");
    setOpenCreateFolderDialog(false);
  };

  const renderTree = (node) => {
    if (node.type === "folder") {
      return (
        <React.Fragment key={node.id}>
          <ListItem
            button
            onClick={() => handleClick(node)}
            onDrop={() => handleDrop(node)}
            onDragOver={(e) => e.preventDefault()}
          >
            <Icon icon="material-symbols:folder-outline" />
            <ListItemText primary={node.name} />
            <IconButton onClick={() => handleDeleteFolder(node)}>
              <Icon icon="mdi:trash-can-outline" />
            </IconButton>
          </ListItem>
          <Collapse in={open.includes(node.id)} timeout="auto" unmountOnExit>
            <List style={{ marginLeft: 16 }} component="div" disablePadding>
              {node.children.map((child) => renderTree(child))}
              <ListItemButton
                onClick={() => setOpenCreateFolderDialog(node.id)}
              >
                <ListItemIcon>
                  <Icon icon="streamline-ultimate:folder-add" />
                </ListItemIcon>
                <ListItemText primary="Neuen Ordner erstellen" />
              </ListItemButton>
            </List>
          </Collapse>
        </React.Fragment>
      );
    }

    if (node.type === "file") {
      return (
        <ListItem
          button
          onClick={() => handleClickFile(node)}
          draggable
          onDragStart={() => handleDragStart(node)}
        >
          <Icon
            icon={FILE_TYPE_ICONS.find((e) => e.type === node?.extension)?.icon}
            fontSize={"175%"}
          />
          <ListItemText primary={node.name} />
        </ListItem>
      );
    }
  };

  // Only allow alphanumeric, space, "_" and "-" characters
  const handleNewFolderNameChange = (e) => {
    const val = e.target.value;
    const filtered = val.replace(/[^A-Za-z0-9 ÖÄÜöäü$ _-]/g, "");
    setNewFolderName(filtered);
  };

  const handleNewFolderPaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData
      .getData("text")
      .replace(/[^A-Za-z0-9 öäü$ _-]/g, "");
    setNewFolderName((prev) => prev + paste);
  };

  return (
    <Box>
      <List>{data && data.map((item) => renderTree(item))}</List>

      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle>Ordner löschen</DialogTitle>
        <DialogContent>
          <p>Möchten Sie die Dateien verschieben oder endgültig löschen?</p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => confirmDeleteFolder(true)}>Verschieben</Button>
          <Button onClick={() => confirmDeleteFolder(false)} color="error">
            Löschen
          </Button>
          <Button onClick={() => setShowDeleteDialog(false)}>Abbrechen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showMoveDialog} onClose={() => setShowMoveDialog(false)}>
        <DialogTitle>Ordnerinhalt verschieben</DialogTitle>
        <DialogContent>
          <p>Wählen Sie den Zielordner:</p>
          <Select
            value={moveTarget}
            onChange={(e) => setMoveTarget(e.target.value)}
            fullWidth
          >
            {flattenFolders(data, 0, deleteFolder?.id).map((folder) => (
              <MenuItem key={folder.id} value={folder.id}>
                {"—".repeat(folder.depth) + " " + folder.name}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={confirmMoveFiles}>Bestätigen</Button>
          <Button onClick={() => setShowMoveDialog(false)}>Abbrechen</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCreateFolderDialog}
        onClose={() => setOpenCreateFolderDialog(false)}
      >
        <DialogTitle>Neuen Unterordner hinzufügen</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Ordnername"
            fullWidth
            value={newFolderName}
            onChange={handleNewFolderNameChange}
            onPaste={handleNewFolderPaste}
            inputProps={{ maxLength: 100 }}
            helperText="Folgende Zeichen sind erlaubt: A-Z, a-z, 0-9, Umlaute, Leerzeichen, _ und -"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenCreateFolderDialog(false)}
            variant="outlined"
            color="error"
          >
            Abbrechen
          </Button>
          <Button onClick={handleAddFolder} variant="contained" color="primary">
            HINZUFÜGEN
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FolderTreeView;
